/**
 * CONTROLLED VOCABULARIES THAT LIVE IN SFMC, NOT IN THIS REPO.
 *
 * Spec: `SPEC-CTA-LABEL-LIST-20260827.md` (Layne, 2026-08-27). The CTA button label
 * dropdown was a hardcoded array here (`echChoice.CTA_25`), so changing the approved
 * vocabulary meant a code deploy. It now lives in a Content Builder block —
 * `ECH_LIST_CTALabels` in "Test - Jack Blocks" — declared the same way the seven
 * `ECH_TPL_*` template blocks are: no email markup, just a `@GUARDRAILS` header this
 * codebase already parses.
 *
 * That is the whole reason a BLOCK beat a data extension: same folder, same
 * `/sfmc-blocks?op=list&deep=1` pull, same parser. No new integration.
 *
 * ── THE SHAPE ────────────────────────────────────────────────────────────────────
 *   { kind: "list", list: "ctaLabels", label: "CTA button labels", strict: true,
 *     groups: [ { group: "Browse inventory", options: ["View Homes", …] }, … ],
 *     usedBy: ["ECH_1CTA.btnLabel", …] }
 *
 * `usedBy` is INFORMATIONAL and nothing here reads it. It exists so that whoever
 * edits the list can see the blast radius in the same file; the fields declare their
 * own reference via `optionsFrom`, and that is what actually drives behaviour.
 *
 * ── ORDER IS DATA ────────────────────────────────────────────────────────────────
 * Groups, and options within a group, render in DECLARED order. Never sorted.
 * "View Homes" before "View Home" before "View All" is a deliberate editorial choice,
 * and alphabetising it would quietly undo a decision someone made.
 *
 * ── WHEN THE FETCH FAILS ─────────────────────────────────────────────────────────
 * Layne's stated preference, and the one this implements: KEEP THE LAST KNOWN LIST
 * and say so. The last good parse of each list is cached in `localStorage`, served
 * when a later pull is missing or unparseable, and flagged stale so the field can
 * show a non-blocking notice. Authors keep working; the staleness is visible.
 *
 * ⛔ THERE IS DELIBERATELY NO HARDCODED FALLBACK LIST. A built-in copy of the
 * vocabulary would silently make this repo the source of truth again the first time a
 * pull hiccuped — which is the exact problem the change exists to solve, and nobody
 * would find out. A first-ever load that fails therefore yields an EMPTY list, and an
 * empty strict dropdown is the correct, visible failure.
 *
 * Same stale-while-revalidate shape as `dupJobs`, so this is the house pattern rather
 * than a new one.
 */
import { parseGrJson, type SfmcBlock } from "./sfmcBlocks";

/** one declared group of options, in declared order */
export interface EchListGroup {
  group: string;
  options: string[];
}

export interface EchList {
  /** the block name, e.g. `ECH_LIST_CTALabels` — what a field's `optionsFrom` names */
  name: string;
  /** the short id the block declares, e.g. `ctaLabels`. Informational. */
  list: string;
  /** human label for the vocabulary, e.g. "CTA button labels" */
  label: string;
  groups: EchListGroup[];
  /** true when the author may not type a custom value */
  strict: boolean;
  /** true when this came from the cache because the live pull had no such block */
  stale: boolean;
}

const LISTS: Record<string, EchList> = {};
const LS_KEY = "echLists:v1";

/** `ECH_LIST_*` is a definition asset, never a placeable module — see registerEchBlocks */
export const isEchListName = (name: unknown): boolean => /^ECH_LIST_/i.test(String(name || ""));

/* ── the localStorage mirror ─────────────────────────────────────────────────── */

type Cached = Record<string, { list: string; label: string; strict: boolean; groups: EchListGroup[] }>;

function readCache(): Cached {
  try {
    const j = JSON.parse(localStorage.getItem(LS_KEY) || "null");
    return j && typeof j === "object" ? (j as Cached) : {};
  } catch { return {}; }
}

function writeCache(c: Cached): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch { /* private mode / quota */ }
}

/* ── parsing ─────────────────────────────────────────────────────────────────── */

/**
 * The declared list on a block, or null.
 *
 * Worker-parsed guardrails first, then the block's own `@GUARDRAILS` comment — the
 * same two-source read `tplDefOf` uses, because the file:// cache path carries the
 * comment only.
 *
 * A block that names itself a list but declares no usable group is REJECTED rather
 * than registered empty: an empty strict dropdown from a malformed block should look
 * like a missing list (which keeps the cache) and not like a valid empty vocabulary
 * (which would overwrite the cache with nothing).
 */
export function echListDefOf(b: SfmcBlock): EchList | null {
  const raw = (b.guardrails as unknown as Record<string, unknown>)
    || (parseGrJson(b.html || "") as Record<string, unknown> | null);
  if (!raw || typeof raw !== "object" || String(raw.kind || "") !== "list") return null;

  const groups: EchListGroup[] = [];
  for (const g of (Array.isArray(raw.groups) ? raw.groups : []) as Record<string, unknown>[]) {
    if (!g || typeof g !== "object") continue;
    const label = String(g.group || "").trim();
    const opts = (Array.isArray(g.options) ? g.options : [])
      .map((o) => String(o == null ? "" : o).trim())
      .filter((o) => o !== "");
    if (!label || !opts.length) continue;
    groups.push({ group: label, options: opts });
  }
  if (!groups.length) return null;

  return {
    name: String(b.name || ""),
    list: String(raw.list || ""),
    label: String(raw.label || ""),
    groups,
    /* absent `strict` means free text is still allowed — the safer default for a
       vocabulary someone is still drafting */
    strict: raw.strict === true,
    stale: false,
  };
}

/**
 * Rebuild the list registry from every `ECH_LIST_*` block in the payload.
 * Mirrors `echLoadTemplates`, and is called from the same place. Returns how many
 * lists loaded LIVE (a cache-restored list does not count — it is not fresh).
 */
export function echLoadLists(blocks: SfmcBlock[]): number {
  Object.keys(LISTS).forEach((k) => { delete LISTS[k]; });
  const cache = readCache();
  let n = 0;

  for (const b of blocks || []) {
    if (!isEchListName(b && b.name)) continue;
    const def = echListDefOf(b);
    if (!def || !def.name) continue;
    LISTS[def.name] = def;
    cache[def.name] = { list: def.list, label: def.label, strict: def.strict, groups: def.groups };
    n++;
  }
  if (n) writeCache(cache);

  /* KEEP THE LAST KNOWN LIST. A pull that simply does not carry the block — the
     block was unpublished, the fetch failed and a stale payload was adopted, the
     folder was renamed — must not empty a vocabulary that worked a minute ago. */
  for (const name of Object.keys(cache)) {
    if (LISTS[name]) continue;
    const c = cache[name];
    if (!c || !Array.isArray(c.groups) || !c.groups.length) continue;
    LISTS[name] = { name, list: c.list, label: c.label, groups: c.groups, strict: !!c.strict, stale: true };
  }
  return n;
}

/* ── reading ─────────────────────────────────────────────────────────────────── */

/** the list a field's `optionsFrom` names, or null when nothing has ever loaded */
export function echList(name: unknown): EchList | null {
  const k = String(name || "");
  return (k && LISTS[k]) || null;
}

/** true when the list is being served from cache — the field should say so */
export function echListStale(name: unknown): boolean {
  const l = echList(name);
  return !!l && l.stale;
}

/** every option across every group, in declared order (dedup, first wins) */
export function echListOptions(name: unknown): string[] {
  const l = echList(name);
  if (!l) return [];
  const out: string[] = [];
  for (const g of l.groups) for (const o of g.options) if (out.indexOf(o) < 0) out.push(o);
  return out;
}

export function echListNames(): string[] { return Object.keys(LISTS); }

/** tests only — drop the in-memory registry (the localStorage mirror is untouched) */
export function __resetEchLists(): void {
  Object.keys(LISTS).forEach((k) => { delete LISTS[k]; });
}

/** tests only — drop the localStorage mirror too */
export function __clearEchListCache(): void {
  try { localStorage.removeItem(LS_KEY); } catch { /* private mode */ }
}
